import React, { memo } from 'react';
import { Drawer, Radio, Checkbox, Space, Button, message } from 'antd';
import { useBundle } from '@amzn/react-arb-tools';
import { safeGetMessage } from '@xbcb/ui-utils';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import { ClassificationState } from './types';
import { DrawerAlert, QuestionBlock, QuestionText, CopyIcon } from './styles';
import { ReasoningSection } from './ReasoningSection';

export interface HtsClassificationDrawerProps {
  classificationState: ClassificationState;
  submitLoading: boolean;
  validateLoading: boolean;
  onClose: () => void;
  onSubmitAnswers: () => void;
  onRestartClassification: () => void;
  onAnswerChange: (questionId: string, value: string[]) => void;
}

export const HtsClassificationDrawer: React.FC<
  HtsClassificationDrawerProps
> = ({
  classificationState,
  submitLoading,
  validateLoading,
  onClose,
  onSubmitAnswers,
  onRestartClassification,
  onAnswerChange,
}) => {
  const [bundle] = useBundle('components.HtsClassification');

  return (
    <Drawer
      title={safeGetMessage(bundle, 'hts_validation_drawer_title')}
      placement="right"
      width={450}
      open={classificationState.drawerOpen}
      onClose={onClose}
      footer={
        classificationState.result ? (
          <Space>
            <Button type="primary" onClick={onClose}>
              {safeGetMessage(bundle, 'hts_return_to_entry')}
            </Button>
            <Button onClick={onRestartClassification} loading={validateLoading}>
              {safeGetMessage(bundle, 'hts_restart_classification')}
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              type="primary"
              onClick={onSubmitAnswers}
              loading={submitLoading}
            >
              {safeGetMessage(bundle, 'hts_submit_answers')}
            </Button>
            <Button onClick={onRestartClassification} loading={validateLoading}>
              {safeGetMessage(bundle, 'hts_restart_classification')}
            </Button>
          </Space>
        )
      }
    >
      {classificationState.result && (
        <DrawerAlert
          type="info"
          showIcon
          message={
            <div>
              <div>
                {safeGetMessage(bundle, 'hts_ai_recommendation_message')}{' '}
                <strong>{classificationState.result.tariffCode}</strong>
                <CopyIcon
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(classificationState.result?.tariffCode || '')
                      .then(() => {
                        void message.success(
                          safeGetMessage(bundle, 'hts_copied_to_clipboard'),
                        );
                      })
                      .catch(() => {
                        /* noop */
                      });
                  }}
                />
                . {safeGetMessage(bundle, 'hts_verify_with_seller')}
              </div>
              {classificationState.result.reasoningTrace?.length ? (
                <ReasoningSection
                  reasoningTrace={classificationState.result.reasoningTrace}
                />
              ) : null}
            </div>
          }
        />
      )}
      {!classificationState.result && (
        <DrawerAlert
          type="warning"
          showIcon
          message={safeGetMessage(
            bundle,
            'hts_classification_additional_info_needed',
          )}
        />
      )}
      {classificationState.error && (
        <DrawerAlert
          type="error"
          showIcon
          message={classificationState.error}
        />
      )}
      {classificationState.questions?.map((question) => (
        <QuestionBlock key={question.id} $dimmed={!!classificationState.result}>
          <QuestionText>{question.text}</QuestionText>
          {question.type === 'MULTI_SELECT' ? (
            <Checkbox.Group
              disabled={!!classificationState.result}
              onChange={(checkedValues: CheckboxValueType[]) =>
                onAnswerChange(question.id, checkedValues as string[])
              }
              value={classificationState.selectedAnswers[question.id] || []}
            >
              <Space direction="vertical">
                {question.options.map((option) => (
                  <Checkbox key={option.id} value={option.id}>
                    {option.text}
                  </Checkbox>
                ))}
              </Space>
            </Checkbox.Group>
          ) : (
            <Radio.Group
              disabled={!!classificationState.result}
              onChange={(e) => onAnswerChange(question.id, [e.target.value])}
              value={classificationState.selectedAnswers[question.id]?.[0]}
            >
              <Space direction="vertical">
                {question.options.map((option) => (
                  <Radio key={option.id} value={option.id}>
                    {option.text}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          )}
        </QuestionBlock>
      ))}
    </Drawer>
  );
};

export default memo(HtsClassificationDrawer);
